/**
 * The MRR as the admin pages read it.
 *
 * The MRR has ONE definition, in SQL: `get_mrr_breakdown()` (migration
 * 20261009221927_mrr_single_definition) — Stripe subscriptions that bill + subscription invoices
 * of the clients without one. The CRM store, the weekly `mrr-snapshot` and Otto's `getMrr` tool
 * all read it; nothing here computes a figure. This module only checks the shape of the RPC
 * answer and maps it to the `MRRResult` the pages already consume.
 */

import { z } from 'zod';

export interface MRRClient {
  name: string;
  source: 'stripe' | 'invoice';
  mrr: number;
  /** The onboarding lead id. */
  id: string;
  detail?: string; // e.g. "10368 €/an" for invoice-based
}

export interface MRRResult {
  total: number;
  arr: number;
  clients: MRRClient[];
  stripeMRR: number;
  invoiceMRR: number;
  /**
   * MRR facturé au mois (abonnements Stripe interval=month, count=1) — la part
   * qui génère réellement un encaissement chaque mois. Exclut les abonnements
   * annuels/semestriels (encaissés en une fois) pour ne pas double-compter une
   * projection mensuelle d'un montant déjà reçu d'un coup.
   */
  recurringMonthly: number;
  /**
   * Leads écartés du MRR car clôturés (churned / lost) alors qu'ils portaient
   * encore un signal d'abonnement. Piste d'audit : permet de vérifier ce que la
   * barrière de clôture a retiré.
   */
  excludedClosedLeadIds: string[];
  /** Clients in onboarding with a subscription that the MRR does not count, and why. */
  leadsWithoutMrr: MrrUncountedLead[];
}

/**
 * A subscribed lead with no MRR line (`leads_without_mrr` of get_mrr_breakdown). The reason is
 * the backend's word, passed through: subscription_not_counted, one_time_invoices_only, no_billing.
 */
export interface MrrUncountedLead {
  /** The onboarding lead id. */
  id: string;
  name: string;
  reason: string;
  /** Invoices that billed outside a subscription category: one may be the contract. */
  unclassifiedInvoiceIds: string[];
}

// Manual-invoice subscription cadences counted as recurring MRR, with the number of months each
// invoice covers. The divisors that count live in get_mrr_breakdown(); this list names the same
// categories for the client-status derivation (clientStatus.ts), which reads raw invoices where the
// MRR is not loaded.
export const INVOICE_MRR_DIVISORS: Record<string, number> = {
  subscription_annual: 12,
  subscription_semestrial: 6,
  subscription_monthly: 1,
};

/** A manual invoice's revenue category, said in words (the Revenue page, a lead's sheet). */
export const INVOICE_CATEGORY_LABELS: Record<string, string> = {
  one_time: 'Ponctuel',
  subscription_annual: 'Abo annuel',
  subscription_semestrial: 'Abo semestriel',
  subscription_monthly: 'Abo mensuel',
  integration: 'Intégration',
  other: 'Autre',
};

/** A manual invoice's category as shown: its own, else what its one-off flag says. */
export function invoiceCategoryOf(invoice: { revenue_category: string | null; is_one_time: boolean | null }): string {
  return invoice.revenue_category || (invoice.is_one_time ? 'one_time' : 'subscription_monthly');
}

/** Writing a category: a subscription cadence counts as recurring, anything else as one-off. */
export function invoiceCategoryPatch(category: string): { revenue_category: string; is_one_time: boolean } {
  return { revenue_category: category, is_one_time: !category.startsWith('subscription') };
}

/**
 * The part of the `get_mrr_breakdown()` document the pages consume. The other keys (the other
 * audit lists, coverage, freshness) are left out: the front does not depend on them.
 */
const mrrBreakdownSchema = z.object({
  total: z.number(),
  arr: z.number(),
  stripe_mrr: z.number(),
  invoice_mrr: z.number(),
  recurring_monthly: z.number(),
  clients: z.array(z.object({
    lead_id: z.string(),
    company_name: z.string(),
    source: z.enum(['stripe', 'invoice']),
    mrr: z.number(),
    detail: z.string().nullable(),
  })),
  excluded_closed_lead_ids: z.array(z.string()),
  // An audit list, not a figure: its absence does not make the MRR unreadable.
  leads_without_mrr: z.array(z.object({
    lead_id: z.string(),
    company_name: z.string(),
    reason: z.string(),
    unclassified_invoice_ids: z.array(z.string()),
  })).default([]),
});

export type MrrReadResult = { ok: true; value: MRRResult } | { ok: false; error: string };

/** Checks the RPC answer and maps it to `MRRResult`, without changing a figure. Pure. */
export function readMrrBreakdown(data: unknown): MrrReadResult {
  const parsed = mrrBreakdownSchema.safeParse(data);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    return { ok: false, error: `réponse inattendue (${issue?.path.join('.') || 'racine'} : ${issue?.message ?? 'invalide'})` };
  }
  const b = parsed.data;
  return {
    ok: true,
    value: {
      total: b.total,
      arr: b.arr,
      stripeMRR: b.stripe_mrr,
      invoiceMRR: b.invoice_mrr,
      recurringMonthly: b.recurring_monthly,
      excludedClosedLeadIds: b.excluded_closed_lead_ids,
      leadsWithoutMrr: b.leads_without_mrr.map((l) => ({
        id: l.lead_id,
        name: l.company_name,
        reason: l.reason,
        unclassifiedInvoiceIds: l.unclassified_invoice_ids,
      })),
      clients: b.clients.map((c) => ({
        id: c.lead_id,
        name: c.company_name,
        source: c.source,
        mrr: c.mrr,
        ...(c.detail !== null ? { detail: c.detail } : {}),
      })),
    },
  };
}
